//  ==========================================================================
//
//  How We Think
//
//  ==========================================================================
import React, { PureComponent } from 'react';

import { graphql } from 'gatsby';

import {
	BlockquoteBreaker,
	CardFeature,
	CardFeatureGroup,
	HeroStandard,
	MetaTags,
	Section,
	SectionWithCards,
	SectionWithHero,
} from '../components';

import { allRoutes, normalizeCuratedContent } from '../helpers';

//  ==========================================================================
//  Component
//  ==========================================================================
export default class HowWeThink extends PureComponent {
	constructor( props ) {
		super( props );

		this._howWeThinkData = {
			description:
				props.data?.allWordpressWpStaticPageMeta.edges?.[0]?.node
					?.how_we_think_meta_desc,
			hero: props.data?.heroImage?.childImageSharp?.fluid,
			heroAlt:
				'Close-up of a glass facade of a building with wooden beams coming from the top in a curved pattern',
			jumpLinks: [
				{
					name: 'Research',
					path: '#research',
				},
				{
					name: 'LINE',
					path: '#line',
				},
				{
					name: 'Citizen HKS',
					path: '#citizen-hks',
				},
				{
					name: 'Well-being',
					path: '#well-being',
				},
				{
					name: 'CADRE',
					path: '#cadre',
				},
			],
			hksLabImage: props.data?.hksLabImage?.childImageSharp?.fluid,
			hksLabContact: {
				post_title: false,
				name: 'Dr. Upali Nanda',
				email: 'research@hksinc.com',
				phone: '+1 832 729 7652',
				path: '/people/upali-nanda'
			},
			hksLabContactResearch: {
				post_title: false,
				name: 'Dr. Upali Nanda',
				email: 'research@hksinc.com',
				phone: '+1 832 729 7652',
				path: '/people/upali-nanda'
			},
			hksLabContactLine: {
				post_title: false,
				name: 'Heath May',
				email: 'hmay@hksinc.com',
				phone: '+1 214 969 3152',
				path: '/people/heath-may'
			},
			hksLabContactCitizen: {
				post_title: false,
				name: 'Nick Cooper',
				email: 'ncooper@hksinc.com',
				phone: '+1 804 200 5301',
				path: '/people/nick-cooper'
			},
			hksLabContactWell: {
				post_title: false,
				name: 'Rand Ekman',
				email: 'rekman@hksinc.com',
				phone: '+1 312 262 9750',
				path: '/people/rand-ekman'
			},
			blockquoteImageHeath: props.data?.blockquoteImageHeath?.childImageSharp?.fluid,
			blockquoteImageLisa: props.data?.blockquoteImageLisa?.childImageSharp?.fluid,
			cadreImage: props.data?.cadreImage?.childImageSharp?.fluid,
			fellowshipsImage: props.data?.fellowshipsImage?.childImageSharp?.fluid,
			featuredData: this._normalizeHowWeThinkData( props ),
		};
	}

	render() {
		const {
			description,
			hero,
			heroAlt,
			jumpLinks,
			hksLabImage,
			hksLabContact,
			hksLabContactResearch,
			hksLabContactLine,
			hksLabContactCitizen,
			hksLabContactWell,
			blockquoteImageHeath,
			blockquoteImageLisa,
			cadreImage,
			fellowshipsImage,
			featuredData: { lineData, hksLabData, researchData, wellData, citizenHksData },
		} = this._howWeThinkData;

		return (
			<main id="main">
				<MetaTags post_title="How We Think" description={description} />

				<HeroStandard
					hero={hero}
					heroAlt={heroAlt}
					label="How We Think"
					post_title="We view design as a process of exploration and discovery fueled by limitless thinking."
					description="Our design research and development teams have learned that we must test, tinker, build and break things to make them better the next time. That’s why research is key to our design practice. It’s a platform for us to experiment, to improve, and to deliver the extraordinary."
					jumpLinks={jumpLinks}
				/>

				<div className="content-contain">
					<Section post_title="hks-lab">
						<SectionWithHero
							video="AyJ1qC3cC0M"
							image={hksLabImage}
							imageAlt="Video still of two men in an office working to build the wood frame of a small square structure"
							label="Explore Further"
							post_title="The HKS Lab"
							description="The HKS Lab is a dedicated space on the seventh floor of our Dallas headquarters devoted to exploring, generating ideas, and advancing design through research. The Lab is a place where design teams, clients, industry professionals and academic partners converge. Together, we connect systematic inquiry with creative thinking, adapting a think-make-test framework."
							cta="The Latest From Our Lab"
							link={`/search?keywords=The Lab`}
							contact={hksLabContact}
						/>
						{hksLabData && 0 < hksLabData.length && (
							<SectionWithCards cardContent={hksLabData} limit="8" />
						)}
					</Section>

					<Section post_title="research">
						<SectionWithCards
							label="Research"
							post_title="How Do We Test the Limits of What Design Can Do?"
							description="We’re looking to raise expectations about how research improves design. Research enables us to create environments based on the best possible insight. It assures our clients that scientific knowledge informs our design decisions. Informed intent and meaningful, measurable impact are foundations for our responsible design approach."
							cardContent={researchData}
							cta="See All Research"
							//link="/search?contentTypes=Research"
							link="/what-we-do/services/research"
							contact={hksLabContactResearch}
							limit="8"
						/>
					</Section>
				</div>

				<BlockquoteBreaker
					image={blockquoteImageHeath}
					imageAlt="Headshot of Heath May"
					quote="Our environments influence our perceptions, blending social, ecological and technological phenomena to encourage a conversation with the world we inhabit."
					name="Heath May"
					post_title={'Director of LINE \nPrincipal \nAIA'.split( '\n' ).map( ( item, i ) => (
						<p key={i}>{item}</p>
					) )}
					linkedin="heathmay"
					alingment="left"
					path="/people/heath-may"
				/>

				<div className="content-group content-contain">
					<Section post_title="line">
						<SectionWithCards
							label="LINE"
							post_title="Can Design Data & Emerging Technology Lead to Innovation?"
							description="LINE, the Laboratory for Intensive Exploration, is an HKS design research team that focuses on the intersection between emerging technologies and architectural methodologies. LINE seeks to elevate modes of architectural production through intensive explorations in systems, materials, processes and tool sets."
							cardContent={lineData}
							cta="See All LINE"
							//link="/search?services=LINE"
							link="/what-we-do/services/line"
							ctaLinkTracking="js-gtm-see-all-link"
							contact={hksLabContactLine}
							limit="8"
						/>
					</Section>

					<Section post_title="citizen-hks" lastItem={true}>
						<SectionWithCards
							label="Citizen HKS"
							post_title="How Can Our Work Improve the World?"
							description="Citizen HKS aims to improve communities in need by leveraging design, community service and financial philanthropy. Our firm’s impact initiative, Citizen HKS is a global platform for socially responsible engagement. Through our work, we aim to improve lives and drive change for communities in need."
							cardContent={citizenHksData}
							cta="See All Citizen HKS"
							link={`/search?keywords=Citizen HKS`}
							ctaLinkTracking="js-gtm-see-all-link"
							contact={hksLabContactCitizen}
							limit="8"
						/>
					</Section>
				</div>

				<BlockquoteBreaker
					image={blockquoteImageLisa}
					imageAlt="Headshot of Lisa Adams"
					quote="Creating spaces that bring joy, comfort and positive connection to underserved communities has deepened my own relationship with design."
					name="Lisa Adams"
					post_title={'Principal \nInterior Designer \nNCIDQ, LEED AP ID+C'
						.split( '\n' )
						.map( ( item, i ) => (
							<p key={i}>{item}</p>
						) )}
					linkedin="lisa-adams-09a7165"
					path="/people/lisa-adams"
				/>

				<div className="content-group content-contain">
					<Section post_title="well-being">
						<SectionWithCards
							label="Well-being"
							post_title="Being & Designing WELL"
							description="Design can improve how people perceive their own well-being, and it can also promote better health. We harness the profound impact of the natural environment to improve well-being. Our goal is to create sustainable environments that are good for the earth and good for people, regenerating and elevating the body and spirit."
							cardContent={wellData}
							cta="See All Well-being"
							link={`/search?keywords=Well-being`}
							ctaLinkTracking="js-gtm-see-all-link"
							contact={hksLabContactWell}
							limit="8"
						/>
					</Section>

					<Section post_title="cadre" lastItem={true}>
						<CardFeatureGroup>
							<CardFeature
								image={cadreImage}
								imageAlt=""
								label="CADRE"
								post_title="A Not-For-Profit Research Group by HKS"
								cta="See Our Work"
								link={allRoutes.cadre}
								ctaLinkTracking="js-gtm-see-all-link"
							/>
							<CardFeature
								image={fellowshipsImage}
								imageAlt="A man and a woman looking at designs laid out on a table in front of them"
								label="Fellowships"
								post_title="Encouraging Curiosity"
								cta="About Our Fellowships"
								link={allRoutes.join_to_fellowships_section}
							/>
						</CardFeatureGroup>
					</Section>
				</div>
			</main>
		);
	}

	_normalizeHowWeThinkData = props => {
		const featured = props.data?.wordsbyCollections || {};

		const {
			citizen_hks = [],
			the_lab = [],
			research_ = [],
			line = [],
			well_being = [],
		} = featured;

		return {
			citizenHksData: citizen_hks.map( ( { relationshipByID } ) =>
				normalizeCuratedContent( relationshipByID )
			),
			hksLabData: the_lab.map( ( { relationshipByID } ) =>
				normalizeCuratedContent( relationshipByID )
			),
			researchData: research_.map( ( { relationshipByID } ) =>
				normalizeCuratedContent( relationshipByID )
			),
			lineData: line.map( ( { relationshipByID } ) =>
				normalizeCuratedContent( relationshipByID )
			),
			wellData: well_being.map( ( { relationshipByID } ) =>
				normalizeCuratedContent( relationshipByID )
			),
		};
	};
}

export const howWeThinkQuery = graphql`
	query HowWeThinkQuery {
		featuredCollections: allWordsbyCollections(filter: { featured: { eq: true } }) {
			nodes {
				post_type
				featured
			}
		}

		allWordpressWpStaticPageMeta: allWordsbyCollections(
			filter: { post_type: { eq: "static_page_meta" } }
		) {
			edges {
				node {
					how_we_think_meta_desc
				}
			}
		}

		heroImage: file(relativePath: { regex: "/HowWeThink-Hero-2020.jpg/" }) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}

		hksLabImage: file(relativePath: { regex: "/how-we-think--2-video.jpg/" }) {
			childImageSharp {
				fluid(maxWidth: 2036) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}

		blockquoteImageHeath: file(
			relativePath: { regex: "/how-we-think--6-quote.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2036) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}

		blockquoteImageLisa: file(
			relativePath: { regex: "/how-we-think--11-quote.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2036) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}

		cadreImage: file(
			relativePath: { regex: "/how-we-think--25-center-for-design.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}

		fellowshipsImage: file(
			relativePath: { regex: "/shared-card-feature--fellowships.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}

		wordsbyCollections(post_type: { eq: "how_we_think" }) {
			the_lab {
				relationshipByID {
					...CardComponent
				}
			}
			research_ {
				relationshipByID {
					...CardComponent
				}
			}
			line {
				relationshipByID {
					...CardComponent
				}
			}
			citizen_hks {
				relationshipByID {
					...CardComponent
				}
			}
			well_being {
				relationshipByID {
					...CardComponent
				}
			}
		}
	}
`;
